import React from 'react';
import { useSelector } from 'react-redux';
import { formatDate as voltoFormatDate } from '@plone/volto/helpers/Utils/Date';
import { toBackendLang } from '@plone/volto/helpers/Utils/Utils';
import { toast } from 'react-toastify';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toast from '@plone/volto/components/manage/Toast/Toast';
import RichTextWidget from '@plone/volto/components/theme/Widgets/RichTextWidget';
import { toPublicURL } from '@plone/volto/helpers/Url/Url';
import { useClipboard } from '@plone/volto/hooks';
import shareSVG from '@plone/volto/icons/share.svg';
import BookingButton from '../../Cart/BookingButton';

type RichTextValue = {
  data?: string;
};

export type ListingItem = {
  '@id'?: string;
  UID?: string;
  adresse?: string;
  aussenstelle?: string;
  bitte_beachten?: string;
  description?: string;
  end_date?: string;
  hotelpreis_tagungspauschale?: number;
  hotelpreis_verpflegung?: number;
  id?: string;
  kontakt?: string;
  kontakt_email?: string;
  kontakt_fax?: string;
  kontakt_name?: string;
  kontakt_ort?: string;
  kontakt_strasse?: string;
  kontakt_telefon?: string;
  nummer?: string;
  programmtext?: RichTextValue;
  raumbezeichnung?: string;
  referenten?: string[];
  referenten_bezeichnung?: string;
  seminar_kategorie_intern?: string;
  seminargebuehr?: number;
  start_date?: string;
  thema?: string;
  thema_zusatz?: string;
  themenplan?: RichTextValue;
  technical_type?: string;
  title?: string;
  uhrzeit_beginn_erster_tag?: string;
  uhrzeit_beginn_regulaer?: string;
  uhrzeit_ende_letzter_tag?: string;
  uhrzeit_ende_regular?: string;
  uhrzeit_ende_regulaer?: string;
  zielgruppen?: string[];
};

const parseDate = (value?: string) => {
  if (!value) return null;

  const date = new Date(value.includes('T') ? value : `${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDate = (value: string | undefined, locale: string) => {
  const date = parseDate(value);
  return date ? voltoFormatDate({ date, locale, format: 'L' }) : null;
};

const formatTime = (value: string | undefined, locale: string) => {
  const date = parseDate(value);

  if (!date) return null;

  return voltoFormatDate({ date, locale, format: 'LT' });
};

export const formatPrice = (value?: number) => {
  if (typeof value !== 'number') return null;

  return `${value.toFixed(2)} €`;
};

export const formatBookingPrice = (value?: number) => {
  if (typeof value !== 'number') return null;

  return Number.isInteger(value) ? `${value},- €` : formatPrice(value);
};

export const sumPrices = (...values: Array<number | undefined>) => {
  const prices = values.filter(
    (value): value is number => typeof value === 'number',
  );
  return prices.length
    ? prices.reduce((sum, value) => sum + value, 0)
    : undefined;
};

const renderText = (text?: string) =>
  text
    ? text
        .split('\n')
        .filter(Boolean)
        .map((line) => <p key={line}>{line}</p>)
    : null;

const Detail = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) =>
  value ? (
    <div className="seminare-detail">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  ) : null;

type SeminareExpandedProps = {
  isOpen: boolean;
  item: ListingItem;
  itemKey: string;
  onClose?: () => void;
};

type ClipboardHookResult = [
  boolean,
  () => Promise<void>,
  React.Dispatch<React.SetStateAction<boolean>>,
];

export const SeminarShareButton = ({ url }: { url: string }) => {
  const [copied, copy, setCopied] = useClipboard(url) as ClipboardHookResult;
  const copyRequested = React.useRef(false);

  React.useEffect(() => {
    if (!copied || !copyRequested.current) return;

    copyRequested.current = false;
    toast.success(
      <Toast
        success
        title="Seminar teilen"
        content="Der Seminar-Link wurde in die Zwischenablage kopiert."
      />,
    );
    setCopied(false);
  }, [copied, setCopied]);

  const copySeminarUrl = () => {
    copyRequested.current = true;
    void copy();
  };

  return (
    <button
      className="seminare-share-button"
      type="button"
      onClick={copySeminarUrl}
    >
      <span>Seminar teilen</span>
      <span aria-hidden="true" className="seminare-share-icon">
        <Icon name={shareSVG} size="20px" />
      </span>
    </button>
  );
};

const SeminareExpanded = ({
  isOpen,
  item,
  itemKey,
  onClose,
}: SeminareExpandedProps) => {
  const [isThemenplanOpen, setIsThemenplanOpen] = React.useState(false);
  const lang = useSelector(
    (state: { intl: { locale: string } }) => state.intl.locale,
  );
  const locale = toBackendLang(lang);
  const startDate = formatDate(item.start_date, locale);
  const endDate = formatDate(item.end_date, locale);
  const startTime = formatTime(
    item.uhrzeit_beginn_erster_tag || item.uhrzeit_beginn_regulaer,
    locale,
  );
  const endTime = formatTime(
    item.uhrzeit_ende_letzter_tag ||
      item.uhrzeit_ende_regulaer ||
      item.uhrzeit_ende_regular,
    locale,
  );
  const location = [item.raumbezeichnung, item.adresse]
    .filter(Boolean)
    .join('\n');
  const contact = [
    item.kontakt,
    item.kontakt_name,
    item.kontakt_strasse,
    item.kontakt_ort,
    item.kontakt_email,
    item.kontakt_telefon,
    item.kontakt_fax,
  ]
    .filter(Boolean)
    .join('\n');
  const accommodationPrice = sumPrices(
    item.hotelpreis_tagungspauschale,
    item.hotelpreis_verpflegung,
  );
  const totalPrice = sumPrices(item.seminargebuehr, accommodationPrice);
  const bookingPrice = formatBookingPrice(totalPrice || item.seminargebuehr);
  const isMuenchenprogramm = item.technical_type === 'muenchenprogramm';
  const singleDayDate = startDate || endDate;
  const singleDayTime =
    startTime && endTime
      ? `${startTime} Uhr bis ${endTime} Uhr`
      : startTime
        ? `${startTime} Uhr`
        : endTime
          ? `bis ${endTime} Uhr`
          : null;
  const bookingType =
    item.technical_type === 'muenchenprogramm' ? 'Kurs' : 'Seminar';
  const feeLabel =
    item.technical_type === 'muenchenprogramm'
      ? 'Teilnahmegebühr'
      : 'Seminargebühr';
  const accommodationLabel =
    item.seminar_kategorie_intern === '1 Tages-Seminar'
      ? 'Tagungspauschale'
      : 'Unterkunft/Verpflegung';
  const seminarId = item.UID;
  const themenplan =
    item.technical_type !== 'muenchenprogramm' && item.themenplan?.data ? (
      <RichTextWidget className="" value={item.themenplan} />
    ) : null;
  const detailUrl = item['@id'] ? toPublicURL(item['@id']) : null;

  return (
    <div
      className="seminare-expanded-shell"
      aria-hidden={!isOpen}
      id={`${itemKey}-content`}
    >
      <div className="seminare-expanded-shell-inner">
        <div className="seminare-expanded">
          {item.programmtext?.data && (
            <RichTextWidget className="" value={item.programmtext} />
          )}

          {themenplan && (
            <div className="seminare-content-toolbar">
              <button
                aria-expanded={isThemenplanOpen}
                aria-controls={`${itemKey}-themenplan`}
                className="seminare-themenplan-toggle"
                type="button"
                onClick={() => setIsThemenplanOpen(!isThemenplanOpen)}
              >
                <span aria-hidden="true" className="seminare-chevron" />
                <span className="read-more">Themenplan</span>
              </button>
            </div>
          )}

          {themenplan && (
            <div
              className={`seminare-themenplan-shell${
                isThemenplanOpen ? ' is-open' : ''
              }`}
              aria-hidden={!isThemenplanOpen}
              id={`${itemKey}-themenplan`}
            >
              <div className="seminare-themenplan-shell-inner">
                <div className="seminare-themenplan-panel">{themenplan}</div>
              </div>
            </div>
          )}

          <div className="seminare-info-grid">
            <dl
              className={`seminare-details${
                isMuenchenprogramm && singleDayDate && singleDayTime
                  ? ' seminare-details-joined-datetime'
                  : ''
              }`}
            >
              {isMuenchenprogramm ? (
                <>
                  <Detail label="Datum" value={singleDayDate} />
                  <Detail label="Uhrzeit" value={singleDayTime} />
                </>
              ) : (
                <Detail
                  label="Datum"
                  value={
                    startDate || endDate ? (
                      <>
                        {startDate && (
                          <>
                            <strong>Vom</strong> {startDate}
                            <strong>{startTime && <>&nbsp;{startTime}</>}</strong>
                          </>
                        )}
                        {endDate && (
                          <>
                            <br />
                            <strong>Bis zum</strong> {endDate}
                            <strong>{endTime && <>&nbsp;{endTime}</>}</strong>
                          </>
                        )}
                      </>
                    ) : null
                  }
                />
              )}
              <Detail
                label={isMuenchenprogramm ? 'Ort/Treffpunkt' : 'Ort'}
                value={renderText(location)}
              />
              <Detail label="Kontakt" value={renderText(contact)} />
              <Detail
                label="Zielgruppe"
                value={renderText(item.zielgruppen?.join('\n'))}
              />
              <Detail
                label="Referent:in"
                value={
                  item.referenten?.length || item.referenten_bezeichnung ? (
                    <div className="seminare-referenten">
                      {item.referenten?.length && (
                        <div className="seminare-referenten-namen">
                          {renderText(item.referenten.join('\n'))}
                        </div>
                      )}
                      {item.referenten_bezeichnung && (
                        <div className="seminare-referenten-bezeichnung">
                          {renderText(item.referenten_bezeichnung)}
                        </div>
                      )}
                    </div>
                  ) : null
                }
              />
              {isMuenchenprogramm && (
                <Detail
                  label="Hinweis"
                  value={renderText(item.bitte_beachten?.trim())}
                />
              )}
            </dl>

            <dl className="seminare-fees">
              <Detail
                label={feeLabel}
                value={formatPrice(item.seminargebuehr)}
              />
              <Detail
                label={accommodationLabel}
                value={formatPrice(accommodationPrice)}
              />
            </dl>
          </div>
          <div className="seminare-expanded-actions">
            {detailUrl && <SeminarShareButton url={detailUrl} />}
            {bookingPrice && (
              <BookingButton
                className="seminare-booking-button"
                seminarId={seminarId}
                courseNumber={item.nummer}
                topic={item.thema}
                startDate={item.start_date}
                endDate={item.end_date}
                price={totalPrice || item.seminargebuehr}
                technicalType={item.technical_type}
              >
                {bookingType} für {formatPrice(totalPrice) || bookingPrice}{' '}
                buchen
              </BookingButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeminareExpanded;
